<template>
  <div 
    class="stat-card"
    :class="{'dark-mode': isDarkMode}"
    @mouseenter="hover = true"
    @mouseleave="hover = false"
  >
    <div class="card-content">
      <div class="icon-container" :class="`bg-${icon.background}`">
        <i class="material-icons" :class="icon.color">{{ icon.name }}</i>
      </div>
      
      <div class="text-content">
        <p class="card-label">{{ title.text }}</p>
        <h3 class="card-value">{{ formattedValue }}</h3>
      </div>
      
      <div class="hover-effect" :class="`hover-${icon.background}`"></div>
    </div>
  </div>
</template>

<style scoped>
/* Modern Color Scheme */
:root {
  --stat-card-1: #4e73df;
  --stat-card-2: #1cc88a;
  --stat-card-3: #f6c23e;
  --stat-card-4: #e74a3b;
  --stat-card-5: #9c27b0;
  --text-white: #ffffff;
  --card-spacing: 12px;
}



.card-content {
  position: relative;
  background: linear-gradient(135deg, var(--card-bg), rgba(255,255,255,0.1));
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  color: var(--text-white);
}

/* Compact Icon Styles */
.icon-container {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.icon-container i {
  font-size: 2rem;
}

/* Gradient Backgrounds */
.bg-primary { background: linear-gradient(135deg, var(--stat-card-1), #6a8aee) !important; }
.bg-secondary { background: linear-gradient(135deg, var(--stat-card-2), #4ddfb1) !important; }
.bg-accent { background: linear-gradient(135deg, var(--stat-card-3), #ffd56e) !important; }
.bg-danger { background: linear-gradient(135deg, var(--stat-card-4), #ff6b5b) !important; }
.bg-info { background: linear-gradient(135deg, var(--stat-card-5), #ba68c8) !important; }

/* Text Styles */
.text-content {
  flex-grow: 1;
  position: relative;
  z-index: 2;
}

.card-label {
  font-size: 1.4rem;
  color: rgba(255,255,255,0.9);
  margin-bottom: 0.5rem;
  font-weight: 600;
  transition: all 0.3s ease;
  font-family: 'DINNextLTArabic-Bold', sans-serif;
}

.card-value {
  font-size: 1.8rem;
  font-weight: 800;
  color: white;
  margin: 0;
  line-height: 1.2;
  transition: all 0.3s ease;
}

/* Hover Effects */
.hover-effect {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: all 0.5s ease;
  background: radial-gradient(circle at center, rgba(255,255,255,0.15) 0%, transparent 70%);
}

.stat-card:hover {
  transform: translateY(-3px);
}

.stat-card:hover .hover-effect {
  opacity: 1;
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .card-content {
    padding: 1.25rem;
  }
  
  .card-value {
    font-size: 1.5rem;
  }
  
  .icon-container {
    width: 50px;
    height: 50px;
  }
  
  .icon-container i {
    font-size: 1.75rem;
  }
}
</style>
<script>
import { mapState, mapGetters } from "vuex";

export default {
  name: "MiniStatisticsCard",
  data() {
    return {
      hover: false
    };
  },
  props: {
    title: {
      type: Object,
      required: true,
      validator: (value) => {
        return typeof value.text === 'string' && 
              (typeof value.value === 'string' || typeof value.value === 'number');
      }
    },
    detail: {
      type: String,
      default: ""
    },
    icon: {
      type: Object,
      required: true,
      default: () => ({
        name: "info",
        color: "text-white",
        background: "primary"
      }),
      validator: (value) => {
        return typeof value.name === 'string' && 
              typeof value.color === 'string' && 
              typeof value.background === 'string';
      }
    },
    directionReverse: {
      type: Boolean,
      default: false
    },
    animateNumber: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    ...mapState(["isRTL"]),
    ...mapGetters(['isDarkMode']),
    formattedValue() {
      if (typeof this.title.value === 'number') {
        return this.title.value.toLocaleString();
      }
      return this.title.value;
    }
  }
};
</script>